import type { Dayjs } from 'dayjs'
import type dayjs from 'dayjs'

import type { GaApp } from '~/modules/app'
import type { RatingEntryModel, RatingPeriodModel } from '~/modules/main'
import {
  mapLeaderboardPeriodsToRatingPeriods,
  mapLeaderboardTopToRatingEntries,
} from '~/modules/main'

/** Формат дат периода в кнопке «итоги» */
const RATING_PERIOD_SHORT_FORMAT = 'DD.MM'

/** Заглушка, когда даты периода не пришли из API */
const RATING_PERIOD_DATE_PLACEHOLDER = 'XX.XX'

/** Заглушка периода в меню профиля */
const RATING_PERIOD_MENU_PLACEHOLDER = 'ХХ.ХХ – ХХ.ХХ'

/** Формат дат периода в меню профиля */
const RATING_PERIOD_MENU_FORMAT = 'DD.MM'

const getRatingPeriodPlaceholder = () => ({
  from: RATING_PERIOD_DATE_PLACEHOLDER,
  to: RATING_PERIOD_DATE_PLACEHOLDER,
})

export class RatingService {
  gaApp: GaApp

  constructor(gaApp: GaApp) {
    this.gaApp = gaApp
  }

  private get dateWrapper(): typeof dayjs {
    return this.gaApp.libs.dateWrapper as typeof dayjs
  }

  isRatingPeriodEmpty(period: RatingPeriodModel): boolean {
    return period.from === '' || period.to === ''
  }

  private parseRatingApiDate(date: string): Dayjs | null {
    const parsed = this.dateWrapper(date)

    if (!parsed.isValid()) {
      return null
    }

    return parsed
  }

  formatRatingPeriodShort(period: RatingPeriodModel) {
    if (this.isRatingPeriodEmpty(period)) {
      return getRatingPeriodPlaceholder()
    }

    const from =
      this.parseRatingApiDate(period.from)?.format(
        RATING_PERIOD_SHORT_FORMAT,
      ) ?? period.from
    const to =
      this.parseRatingApiDate(period.to)?.format(RATING_PERIOD_SHORT_FORMAT) ??
      period.to

    return { from, to }
  }

  formatRatingCurrentPeriod(period: RatingPeriodModel) {
    if (this.isRatingPeriodEmpty(period)) {
      return getRatingPeriodPlaceholder()
    }

    const fromDate = this.parseRatingApiDate(period.from)
    const toDate = this.parseRatingApiDate(period.to)

    if (fromDate === null || toDate === null) {
      return getRatingPeriodPlaceholder()
    }

    return {
      from: fromDate.format(RATING_PERIOD_SHORT_FORMAT),
      to: toDate.format(RATING_PERIOD_SHORT_FORMAT),
    }
  }

  formatRatingPeriodMenu(period: RatingPeriodModel): string {
    if (this.isRatingPeriodEmpty(period)) {
      return RATING_PERIOD_MENU_PLACEHOLDER
    }

    const fromDate = this.parseRatingApiDate(period.from)
    const toDate = this.parseRatingApiDate(period.to)

    if (fromDate === null || toDate === null) {
      return RATING_PERIOD_MENU_PLACEHOLDER
    }

    const from = fromDate.format(RATING_PERIOD_MENU_FORMAT)
    const to = toDate.format(RATING_PERIOD_MENU_FORMAT)

    return `${from} – ${to}`
  }

  async loadCurrentLeaderboard(): Promise<RatingEntryModel[]> {
    const { current, previous } = this.gaApp.stores.main.rating

    current.isLoading = true
    current.isError = false

    try {
      const response =
        await this.gaApp.services.main.api.getLeaderboardCurrent()
      const entries = mapLeaderboardTopToRatingEntries(response.top)
      const periods = mapLeaderboardPeriodsToRatingPeriods(response)

      current.entries = entries
      current.periods = periods.current
      current.currentUser = response.currentUser ?? null
      previous.periods = periods.previous

      return entries
    } catch (error) {
      current.isError = true
      current.entries = []
      current.periods = { from: '', to: '' }
      current.currentUser = null

      this.gaApp.services.app.rejection.log({
        name: 'main:get-leaderboard-current',
        message: 'Ошибка получения текущего рейтинга',
        errorData: error,
      })

      return []
    } finally {
      current.isLoading = false
    }
  }

  async loadPreviousLeaderboard(): Promise<RatingEntryModel[]> {
    const { previous } = this.gaApp.stores.main.rating

    previous.isLoading = true
    previous.isError = false

    try {
      const response =
        await this.gaApp.services.main.api.getLeaderboardPrevious()
      const entries = mapLeaderboardTopToRatingEntries(response.top)

      previous.entries = entries
      previous.periods = mapLeaderboardPeriodsToRatingPeriods(response).previous
      previous.modalTitle = response.ratingPreviousTitle ?? ''
      previous.modalText = response.ratingPreviousText ?? ''
      previous.currentUser = response.currentUser ?? null

      return entries
    } catch (error) {
      previous.isError = true
      previous.entries = []
      previous.modalTitle = ''
      previous.modalText = ''
      previous.currentUser = null

      this.gaApp.services.app.rejection.log({
        name: 'main:get-leaderboard-previous',
        message: 'Ошибка получения прошлого рейтинга',
        errorData: error,
        userNotification: this.gaApp.i18n.t('main.rating.previousLoadError'),
      })

      return []
    } finally {
      previous.isLoading = false
    }
  }
}
